import { Box, Flex, Text } from '@chakra-ui/react';
import { Column } from '@gamma/ui/layout';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import { graphqlErrorRedirects } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import { useUserSession } from '@investigator/shared/hooks';
import { SanitizedMlAlert } from '../../../detections/pages';
import {
  FeatureMinMaxData,
  ModelSummaryContribution,
  useGetModelSummaries,
} from '../../../detections/queries';

import {
  BoxPlotChart,
  BoxPlotChartLegend,
  smartNumber,
} from '../../../investigations/components';

export interface EntityVsPopChartProps {
  alertData?: SanitizedMlAlert;
  features?: ModelSummaryContribution[];
}

export const EntityVsPopChart = ({
  alertData,
  features,
}: EntityVsPopChartProps) => {
  const { title, constantValue } =
    i18n.pages.entityDetails.ml.analysis.cards.entityVsPop;

  const { signOut } = useUserSession();

  const { loading, error, data } = useGetModelSummaries({
    variables: { model_ids: alertData?.models?.[0]?.model_name },
  });

  if (loading || error) {
    return (
      <Column col={7} display="flex" flexDir="column">
        <Flex alignItems="start" justifyContent="center" flexGrow={1}>
          <GraphQLReqStatus
            loading={true}
            error={error}
            signOut={signOut}
            extended={graphqlErrorRedirects}
          />
        </Flex>
      </Column>
    );
  }

  const modelSummaries = data?.getModelSummaries?.[0];

  if (!modelSummaries) {
    return null;
  }

  return (
    <Column
      col={7}
      display="flex"
      flexDir="column"
      justifyContent="space-between"
      data-testid="entity-vs-population-chart"
    >
      <Text textStyle="h6" data-testid="entity-vs-pop-chart-title">
        {title}
      </Text>
      <Flex direction="column">
        <BoxPlotChartLegend />
        <Box paddingTop="28px">
          {features?.map(
            ({ actual, difference, display_name, feature_name }) => {
              const featureMinMax =
                modelSummaries?.feature_details?.feature_minmax?.find(
                  (feature: FeatureMinMaxData) => feature.name === feature_name,
                );

              const boxPlotData = {
                ...featureMinMax,
                actual,
                difference,
                display_name,
              };

              return actual === 0 ? (
                <Flex alignItems="center" key={feature_name} height="50px">
                  <Box mr={1} w="40px" textAlign="right">
                    {smartNumber(actual)}
                  </Box>
                  <Text
                    textStyle="body-md"
                    flexGrow={1}
                    padding="3px 0"
                    textAlign="center"
                    border="1px dashed rgba(255, 255, 255, 0.3)"
                    data-testid={`entity-vs-pop-chart-constant-value-${feature_name}`}
                  >
                    {constantValue}
                  </Text>
                  <Box ml={1} w="40px"></Box>
                </Flex>
              ) : (
                <Flex key={feature_name} height="50px" alignItems="center">
                  <BoxPlotChart
                    id={feature_name}
                    data={boxPlotData}
                    tooltipPosition="top"
                  />
                </Flex>
              );
            },
          )}
        </Box>
      </Flex>
    </Column>
  );
};
